import { ArrowRight, Shield, Clock, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

export default function FoundingOffer() {
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState("nz");

  // Detect region from timezone (simple heuristic)
  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.includes("Australia")) {
      setSelectedRegion("au");
    }
  }, []);

  const pricing = {
    nz: { price: "NZ$595", label: "New Zealand" },
    au: { price: "A$545", label: "Australia" },
  };

  const handleSubmitTender = () => {
    navigate("/tender-red-review#tender-form");
  };

  return (
    <section
      id="founding-offer"
      className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
      data-testid="founding-offer-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left - Offer Details */}
          <div>
            <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
              Limited Availability
            </span>
            <h2
              className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4 mb-6"
              data-testid="founding-offer-title"
            >
              Founding Review Offer
            </h2>

            {/* Price Display */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <button
                  onClick={() => setSelectedRegion("nz")}
                  className={`px-4 py-2 font-mono text-sm uppercase tracking-wider border-2 transition-colors ${
                    selectedRegion === "nz"
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-black hover:bg-zinc-100"
                  }`}
                  data-testid="region-nz"
                >
                  NZ
                </button>
                <button
                  onClick={() => setSelectedRegion("au")}
                  className={`px-4 py-2 font-mono text-sm uppercase tracking-wider border-2 transition-colors ${
                    selectedRegion === "au"
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-black hover:bg-zinc-100"
                  }`}
                  data-testid="region-au"
                >
                  AU
                </button>
              </div>
              <p
                className="font-mono text-4xl md:text-5xl font-bold"
                data-testid="founding-price"
              >
                {pricing[selectedRegion].price}{" "}
                <span className="text-lg text-zinc-500">excl. GST</span>
              </p>
            </div>

            {/* Key Points */}
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-black mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-body font-medium">Target 24-hour turnaround</p>
                  <p className="font-body text-sm text-zinc-600">
                    Timing confirmed before the review begins
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-black mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-body font-medium">Substantially complete tenders</p>
                  <p className="font-body text-sm text-zinc-600">
                    Best suited to small-to-mid-sized construction tenders
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-black mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-body font-medium">Founding Guarantee</p>
                  <p className="font-body text-sm text-zinc-600">
                    If the review does not identify at least one item worth checking before submission, there is no review fee
                  </p>
                </div>
              </div>
            </div>

            <p className="font-body text-zinc-600 text-sm mb-8 border-l-4 border-black pl-4">
              Available for the first 10 accepted standard reviews across New Zealand and Australia combined. Larger, incomplete or unusually complex packages are quoted separately before work begins.
            </p>

            <button
              onClick={handleSubmitTender}
              className="bg-black text-white h-14 px-8 uppercase font-bold tracking-widest border-2 border-black btn-brutal flex items-center gap-3 group hover:bg-zinc-800"
              data-testid="founding-offer-cta"
            >
              Submit a Tender for Review
              <ArrowRight
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>

          {/* Right - Visual */}
          <div className="relative">
            <div className="border-4 border-black bg-white p-8 md:p-10">
              <h3 className="font-heading font-bold text-xl uppercase mb-6">
                What You Receive
              </h3>
              <ul className="space-y-3">
                {[
                  "Executive review summary",
                  "GO / GO WITH ACTIONS / HOLD decision",
                  "Prioritised RED, AMBER and GREEN findings",
                  "Scope-to-price coverage findings",
                  "Estimate integrity and commercial calculation findings",
                  "Quote and exclusion gaps",
                  "Build-up, productivity and programme concerns",
                  "Evidence references back to your review pack",
                  "Recommended actions and RFIs",
                  "Final pre-submission checklist",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 flex-shrink-0 bg-black" />
                    <span className="font-body text-zinc-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-4 border-black bg-zinc-200 -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
